import React, { useMemo, useState } from 'react';
import { Search } from '@appkit4/react-components/search';
import { Button } from '@appkit4/react-components/button';
import { Loading } from '@appkit4/react-components/loading';
import { type ColumnDef } from '@tanstack/react-table';
import Pagination from '../../components/reusables/Pagination';
import DataTable from '../../components/reusables/DataTable';
import { useEngagement } from '../../context/EngagementContext';
import { useGetAuditLog } from '../../api/generated/endpoint';
import type { AuditLogEntryResponse } from '../../api/generated/models/auditLogEntryResponse';

const PAGE_SIZE_DEFAULT = 25;

const Log = () => {
    const { activeTimeId, selectedEntityId } = useEngagement();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(
        null
    );

    const handleSearchChange = (value: string) => {
        setSearch(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(value);
            setCurrentPage(1);
        }, 400);
    };

    const skip = (currentPage - 1) * pageSize;

    const { data, isLoading } = useGetAuditLog(
        {
            EntityId: selectedEntityId!,
            TimeId: activeTimeId!,
            Skip: skip,
            Take: pageSize,
            SearchString: debouncedSearch || undefined,
        },
        {
            query: {
                enabled: !!selectedEntityId && !!activeTimeId,
                keepPreviousData: true,
            },
        }
    );

    const items = [...(data?.items ?? [])];
    const totalCount = debouncedSearch
        ? (data?.filteredCount ?? 0)
        : (data?.totalCount ?? 0);

    const logColumns: ColumnDef<AuditLogEntryResponse, any>[] = useMemo(
        () => [
            {
                accessorKey: 'actionedOn',
                header: 'Date/Time',
                cell: ({ getValue }) => {
                    const val = getValue();
                    return val
                        ? new Date(val).toLocaleString('en-GB', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                          })
                        : '';
                },
            },
            { accessorKey: 'userName', header: 'User' },
            { accessorKey: 'action', header: 'Action' },
            { accessorKey: 'type', header: 'Audit Event' },
            { accessorKey: 'logText', header: 'Audit Detail' },
        ],
        []
    );

    const handleExport = () => {
        // TODO: call backend endpoint to generate/download audit log file
    };

    if (isLoading) {
        return (
            <div
                className="log-page"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '2rem',
                }}
            >
                <Loading loadingType="circular" indeterminate />
            </div>
        );
    }

    return (
        <div className="log-page">
            <div className="log-header-row">
                <h3 className="log-title">Log</h3>
                <Button kind="primary" onClick={handleExport}>
                    Export Audit Log
                </Button>
            </div>

            <div className="log-search-row">
                <Search
                    className="px-0"
                    placeholder="Search"
                    value={search}
                    onChange={(value) => handleSearchChange(value)}
                />
            </div>

            <div className="log-table-wrapper">
                <DataTable data={items} columns={logColumns} sorting />
            </div>

            <div className="log-pagination">
                <Pagination
                    totalItems={totalCount}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    onPageChange={(page) => setCurrentPage(page)}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setCurrentPage(1);
                    }}
                />
            </div>
        </div>
    );
};

export default Log;
